import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { alpha, useTheme } from '@mui/material/styles';

import {
  useAlertSettingsQuery,
  useSaveAlertSettingsMutation,
  useSendTestAlertMutation,
} from '../../api/queries';
import { useErrorSnackbar } from '../ErrorSnackbar';
import WriteGuard from '../WriteGuard';
import { useWritePermission } from '../../api/permission-queries';

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AlertSettingsSection() {
  const theme = useTheme();
  const permission = useWritePermission();
  const { showError } = useErrorSnackbar();
  const { data, isLoading, isError } = useAlertSettingsQuery();
  const saveMutation = useSaveAlertSettingsMutation();
  const testMutation = useSendTestAlertMutation();

  // Form state
  const [recipients, setRecipients] = useState('');
  const [cooldownHours, setCooldownHours] = useState(4);
  const [enabled, setEnabled] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // UI state
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [testResult, setTestResult] = useState<{
    sent: boolean;
    reason?: string;
    clientCount?: number;
    issueCount?: number;
  } | null>(null);

  // Sync form state from query data on first load (guard prevents overwriting unsaved edits)
  /* eslint-disable react-hooks/set-state-in-effect -- intentional: one-time form initialization from API data */
  useEffect(() => {
    if (data && !initialized) {
      setRecipients(data.recipients.join(', '));
      setCooldownHours(data.cooldownHours);
      setEnabled(data.enabled);
      setInitialized(true);
    }
  }, [data, initialized]);
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    if (isError) {
      showError('Failed to load alert settings');
    }
  }, [isError, showError]);

  async function handleSave() {
    const parsed = recipients
      .split(/[,\n]/)
      .map((r) => r.trim())
      .filter(Boolean);

    try {
      await saveMutation.mutateAsync({
        recipients: parsed,
        cooldownHours,
        enabled,
      });
      setSaveSuccess(true);
    } catch {
      showError('Failed to save alert settings');
    }
  }

  async function handleTestAlert() {
    try {
      const result = await testMutation.mutateAsync();
      setTestResult(result);
    } catch {
      showError('Failed to send test alert');
    }
  }

  if (isLoading && !data) {
    return (
      <Box sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={280} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={100} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Status Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Chip
              label={data?.isConfigured ? 'Configured' : 'Not Configured'}
              size="small"
              sx={{
                bgcolor: alpha(
                  data?.isConfigured ? theme.palette.success.main : theme.palette.error.main,
                  0.12
                ),
                color: data?.isConfigured ? theme.palette.success.main : theme.palette.error.main,
                fontWeight: 600,
                fontSize: '0.75rem',
                border: `1px solid ${alpha(
                  data?.isConfigured ? theme.palette.success.main : theme.palette.error.main,
                  0.2
                )}`,
              }}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}
          >
            Sender: {data?.senderAddress || 'Not configured'}
          </Typography>
          {data?.savedSettings && (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.75rem' }}
            >
              Last updated: {formatDate(data.savedSettings.updatedAt)} by{' '}
              {data.savedSettings.updatedBy}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Settings Card */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="h6" sx={{ fontSize: '0.95rem', mb: 2 }}>
            Configuration
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                color="primary"
              />
            }
            label="Email alerts enabled"
            sx={{ mb: 2, display: 'flex' }}
          />

          <TextField
            label="Recipients (one per line or comma-separated)"
            multiline
            rows={3}
            fullWidth
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            label="Cooldown hours"
            type="number"
            value={cooldownHours}
            onChange={(e) => setCooldownHours(Number(e.target.value))}
            helperText="Minimum hours between duplicate alerts"
            slotProps={{ htmlInput: { min: 1 } }}
            sx={{ mb: 2.5, width: 200 }}
          />

          <Box>
            <WriteGuard reason={permission.reason}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saveMutation.isPending || !permission.canWrite}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
              </Button>
            </WriteGuard>
          </Box>
        </CardContent>
      </Card>

      {/* Test Alert Card */}
      <Card>
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Typography variant="h6" sx={{ fontSize: '0.95rem', mb: 2 }}>
            Test Alert
          </Typography>

          <WriteGuard reason={permission.reason}>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleTestAlert}
              disabled={testMutation.isPending || !data?.isConfigured || !permission.canWrite}
            >
              {testMutation.isPending ? 'Sending...' : 'Send Test Alert'}
            </Button>
          </WriteGuard>

          {testResult && (
            <Typography
              variant="body2"
              sx={{
                mt: 1.5,
                fontFamily: 'var(--font-mono)',
                fontSize: '0.8rem',
                color: testResult.sent ? 'success.main' : 'text.secondary',
              }}
            >
              {testResult.sent
                ? `Alert sent — ${testResult.issueCount ?? 0} issues across ${testResult.clientCount ?? 0} clients`
                : (testResult.reason ?? 'No critical issues to report')}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={4000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSaveSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: '100%' }}
        >
          Alert settings saved successfully
        </Alert>
      </Snackbar>
    </Box>
  );
}
